import ClearIcon from "@mui/icons-material/Clear"
import { Box, Button } from "@mui/material"
import Typography from "@mui/material/Typography"
import { makeStyles } from "tss-react/mui-compat"
import { SHOW_VIDEO_COUNT } from "app-constants/constants"
import { createPath, createURL_SSR, ROUTE } from "app-constants/routing"
import { ActivityIndicator } from "components/activity-indicator"
import { AttachmentsTagsMobile } from "components/attachments/attachments-tags-mobile"
import { ContentContainer } from "components/content-container"
import MainLayout from "components/main-layout"
import { ProfileHeader } from "components/profile-header"
import { StickyRightColumn } from "components/sticky-right-column"
import { Tag as TagComponent } from "components/tag"
import { Text } from "components/text"
import { spacing } from "deprecated-theme"
import useAuthRedirect from "hooks/useAuthRedirect"
import useHeadingStyles from "hooks/useHeadingStyles"
import { api } from "lib/api"
import buildServerSidePage from "lib/build-server-side-page"
import buildServerSideProps from "lib/build-server-side-props"
import findIncludedRelationship from "lib/find-included-relationship"
import { getConventionVideoViewCountsMap } from "lib/getConventionVideoViewCounts"
import applyCache from "lib/mw/apply-cache"
import applyCurrentServerContext from "lib/mw/apply-current-server-context"
import applyNextSeo from "lib/mw/apply-next-seo"
import applyTitle from "lib/mw/apply-title"
import openWindowAsync from "lib/open-window-async"
import { Institution, Video, Attachment, Tag } from "lib/types"
import useCurrent from "lib/use-current"
import useDeprecatedApiMethod from "lib/use-deprecated-api-method"
import ConventionVideo from "models/convention-api/v1/ConventionVideo"
import { useRouter } from "next/router"
import pluralize from "pluralize"
import React, { useEffect, useState } from "react"
import Modal from "react-modal"
import ReactPlayer from "react-player"
import { isNilOrEmpty, pxToRem } from "utils/helpers"

export const getServerSideProps = buildServerSideProps(
  applyCache(),
  applyCurrentServerContext(),
  [
    async (context) => {
      await ConventionVideo.applyOne("video", {
        filter: {
          id: context.params.video_id as string,
        },
      })(context)
    },
  ],
  applyTitle("Videos"),
  applyNextSeo((context) => ({
    openGraph: {
      title: context.props.video.name,
      url: createURL_SSR(context, {
        path: ROUTE.VIDEO,
        params: { videoId: context.props.video.id },
      }),
      type: "website",
      images: [
        {
          url: context.props.video.thumbnail_url,
          alt: context.props.video.name,
        },
      ],
    },
  })),
)

const useStyles = makeStyles()({
  modalStyle: {
    inset: 0,
    background: "none",
  },
  warningTextStyle: {
    color: "white",
    textAlign: "center",
    width: "90%",
    margin: "2rem auto",
    fontFamily: "Avenir",
    fontSize: "16px",
  },
});

interface VideoPageProps {
  video: ConventionVideo
}

export default buildServerSidePage(function VideoPage(props: VideoPageProps) {
  const { video: videoSSR } = props
  const { query, push, asPath, back } = useRouter()
  const [videoViewCount, setVideoViewCount] = useState<number | null>(null)
  const exhibitorId = videoSSR.provider_exhibitor_id

  const authRedirect = useAuthRedirect()

  const current = useCurrent()

  const { classes: headingClasses } = useHeadingStyles()

  const { classes } = useStyles()
  const { data, error } = useDeprecatedApiMethod("getVideo", [query.video_id])

  // @ts-ignore
  useEffect(() => {
    if (!current && videoViewCount !== null) return undefined
    getConventionVideoViewCountsMap(current, `filter[id]=${query.video_id}`)
      .then((result) => {
        setVideoViewCount(result[`${query.video_id}`] ?? 0)
      })
      .catch((error) => {
        console.error(error)
        setVideoViewCount(0)
      })
  }, [current])

  if (error) return <div>something went wrong...</div>

  if (!data || videoViewCount === null) {
    return (
      <MainLayout layout="macro">
        <ActivityIndicator />
      </MainLayout>
    )
  }

  const video: Video = data.data

  const includedInstitution: Institution = data
    ? findIncludedRelationship(data.included, video.relationships.institution.data)
    : null

  const attachments = data.included.filter(
    (inc: Attachment) => inc.type === "convention_video_attachment",
  )

  const tags = data.included.filter((inc: Tag) => inc.type === "convention_tag")

  const onHeaderPress = () => {
    authRedirect(async () => {
      await push(
        {
          pathname: `/videos/[video_id]`,
          query: {
            play: true,
          },
        },
        {
          pathname: asPath,
          query: {
            play: true,
          },
        },
      )

      try {
        await api.createVideoView({ req: undefined }, video.id)
      } catch (error) {
        console.error(error)
      }
    })
  }

  const onInstitutionPress = () =>
    authRedirect(() => {
      push(createPath({ path: ROUTE.EXHIBITOR, params: { exhibitorId } }))
    })

  const onAttachmentPress = (attachment: Attachment) =>
    authRedirect(async () =>
      openWindowAsync(async () => {
        await api.createVideoAttachmentView({ req: undefined }, attachment.id)
        return attachment.attributes.url
      }),
    )

  if (!includedInstitution) {
    return <></>
  }

  return (
    <MainLayout layout="macro">
      {/*TODO: refactor it. Replace it with VideoPlayer component*/}
      {/*@ts-ignore children type error occurs because of upgrading to React 18*/}
      <Modal
        preventScroll
        className={classes.modalStyle}
        isOpen={!!query.play}
        onRequestClose={() => back()}
        contentLabel="Video Modal">
        <div className={classes.warningTextStyle}>
          <span tabIndex={0}>
            Videos are hosted by a third party, and may lack audio description, closed captions or
            transcripts.
          </span>
          <Button onClick={() => back()} color="inherit" startIcon={<ClearIcon />}>
            Close
          </Button>
        </div>

        <ReactPlayer controls={true} url={video.attributes.url} width="100%" height="100%" />
      </Modal>
      <ContentContainer>
        <ProfileHeader
          onPress={onHeaderPress}
          videoURL={video.attributes.url}
          imageURL={video.attributes.thumbnail_url}
          title={video.attributes.name}
        />

        <Box
          display="flex"
          marginBottom={pxToRem(20)}
          flexDirection={["column", "column", "column", "row"]}
          width="100%"
          alignItems="flex-start"
          justifyContent="space-between"
          pt={[4, 4, 5]}
          px={[3, 3, 4]}>
          <Box display="flex" flexDirection="column" width={["100%", "100%", "50%", "70%"]}>
            <Text
              preset="bodyMediumPrimary"
              text={includedInstitution.attributes.name}
              onPress={onInstitutionPress}
            />
            <Box mt={[2]}>
              <Typography variant="h1" className={headingClasses.h1}>
                {video.attributes.name}
              </Typography>
            </Box>

            {SHOW_VIDEO_COUNT && (
              <Box marginTop={pxToRem(16)}>
                <Text
                  preset="bodyMediumPrimary"
                  text={`${videoViewCount} ${pluralize("VIEW", videoViewCount)}`}
                />
              </Box>
            )}
          </Box>
          <AttachmentsTagsMobile
            onViewPress={onAttachmentPress}
            tags={tags}
            attachments={attachments}
          />

          <StickyRightColumn>
            {!isNilOrEmpty(attachments) && (
              <>
                <Text preset="bodyLargePrimary" text="Additional Resources" />
                {attachments.map((attachment: Attachment) => {
                  return (
                    <Box
                      key={attachment.id}
                      marginTop={pxToRem(16)}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center">
                      <Text preset="bodyMediumPrimary" text={attachment.attributes.caption} />
                      <Button
                        variant="outlined"
                        onClick={() => onAttachmentPress(attachment)}
                        style={{
                          width: 140,
                          marginLeft: spacing[2],
                        }}>
                        VIEW
                      </Button>
                    </Box>
                  )
                })}
              </>
            )}

            <Box display="flex" flexDirection="row" flexWrap="wrap" marginTop={pxToRem(16)}>
              {tags.map((tag: Tag) => {
                return <TagComponent key={tag.id} text={tag.attributes.name} />
              })}
            </Box>
          </StickyRightColumn>
        </Box>
      </ContentContainer>
    </MainLayout>
  )
})
